import { CheckCircleOutline, DoneAll, HighlightOff, QrCode2, ReceiptLong } from "@mui/icons-material";
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Paper, TextField, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { StatusRodadaPedido, StatusRodadaPedidoCor, StatusRodadaPedidoTitulo } from "../../enum/StatusRodadaPedido";
import { useMesasQRCode } from "../../hooks/useMesasQRCode";
import { IPedido } from "../../interface/IPedido";
import { IRodadaPedido } from "../../interface/IRodadaPedido";
import { ISessaoMesa, StatusSessaoMesa } from "../../interface/ISessaoMesa";

interface RodadasMesaPaperProps {
    pedido: IPedido;
    recarregar: () => Promise<void>;
}

/**
 * Pedidos que os clientes enviaram pelo QR Code da mesa, um bloco por rodada.
 * Os produtos já estão na lista do pedido; aqui o salão confirma (manda para a cozinha),
 * recusa (retira os itens da conta) ou marca como entregue.
 */
export const RodadasMesaPaper = ({ pedido, recarregar }: RodadasMesaPaperProps) => {
    const { confirmarRodada, recusarRodada, entregarRodada, listarSessoes, reabrirSessao, loading } = useMesasQRCode();
    const [sessao, setSessao] = useState<ISessaoMesa>();
    const [recusando, setRecusando] = useState<string>();
    const [motivo, setMotivo] = useState("");
    const [processando, setProcessando] = useState<string>();

    useEffect(() => {
        listarSessoes().then(sessoes => setSessao(sessoes.find(item => item.pedidoUuid === pedido.uuid)));
    }, [pedido]);

    const rodadas = useMemo(
        () => [...(pedido.rodadas ?? [])].sort((a, b) => b.numero - a.numero),
        [pedido.rodadas]
    );

    if (!rodadas.length && !sessao) {
        return null;
    }

    const executar = async (rodada: IRodadaPedido, acao: () => Promise<unknown>) => {
        setProcessando(rodada.uuid);
        await acao();
        setProcessando(undefined);
        setRecusando(undefined);
        setMotivo("");
        await recarregar();
    };

    const reabrir = async () => {
        if (!sessao) return;
        const atualizada = await reabrirSessao(sessao.uuid);
        if (atualizada) setSessao(atualizada);
    };

    return (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, borderRadius: "12px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <QrCode2 color="primary" />
                <Typography fontWeight={700}>Pedidos pelo QR Code</Typography>
            </Box>

            {sessao && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Na mesa: {sessao.participantes.map(participante => participante.nome).join(", ") || "-"}
                </Typography>
            )}

            {sessao?.status === StatusSessaoMesa.CONTA_SOLICITADA && (
                <Alert
                    severity="warning"
                    icon={<ReceiptLong />}
                    sx={{ mb: 1 }}
                    action={
                        <Button color="inherit" size="small" onClick={reabrir} disabled={loading}>
                            Reabrir
                        </Button>
                    }
                >
                    Conta solicitada{sessao.contaSolicitadaPor ? ` por ${sessao.contaSolicitadaPor}` : ""}
                    {sessao.contaSolicitadaAs ? ` às ${moment(sessao.contaSolicitadaAs).format("HH:mm")}` : ""}
                </Alert>
            )}

            {!rodadas.length && (
                <Typography variant="body2" color="text.secondary">Nenhum pedido enviado pela mesa ainda.</Typography>
            )}

            {rodadas.map(rodada => {
                const produtos = pedido.produtos?.filter(produto => produto.rodadaUuid === rodada.uuid) ?? [];
                const ocupado = processando === rodada.uuid;

                return (
                    <Box key={rodada.uuid} sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, py: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" fontWeight={700}>
                                #{rodada.numero} · {rodada.participanteNome ?? "Cliente"}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    {moment(rodada.criadaAs).format("HH:mm")}
                                </Typography>
                            </Typography>
                            <Chip size="small" color={StatusRodadaPedidoCor[rodada.status]} label={StatusRodadaPedidoTitulo[rodada.status]} />
                        </Box>

                        {rodada.status === StatusRodadaPedido.CANCELADA ? (
                            <Typography variant="caption" color="text.secondary">
                                {rodada.motivoCancelamento || "Recusada pelo salão"}
                            </Typography>
                        ) : produtos.map(produto => (
                            <Typography key={produto.uuid} variant="caption" display="block">
                                {produto.quantidade ?? 1}x {produto.descricao}
                            </Typography>
                        ))}

                        {ocupado ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <>
                                {rodada.status === StatusRodadaPedido.AGUARDANDO_CONFIRMACAO && (
                                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<CheckCircleOutline />}
                                            onClick={() => executar(rodada, () => confirmarRodada(pedido.uuid, rodada.uuid))}
                                        >
                                            Confirmar
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<HighlightOff />}
                                            onClick={() => setRecusando(recusando === rodada.uuid ? undefined : rodada.uuid)}
                                        >
                                            Recusar
                                        </Button>
                                    </Box>
                                )}
                                {rodada.status === StatusRodadaPedido.EM_PREPARO && (
                                    <Button
                                        size="small"
                                        sx={{ mt: 1 }}
                                        startIcon={<DoneAll />}
                                        onClick={() => executar(rodada, () => entregarRodada(pedido.uuid, rodada.uuid))}
                                    >
                                        Marcar como entregue
                                    </Button>
                                )}
                                <Collapse in={recusando === rodada.uuid}>
                                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Motivo (aparece para o cliente)"
                                            value={motivo}
                                            onChange={evento => setMotivo(evento.target.value)}
                                        />
                                        <Button
                                            size="small"
                                            color="error"
                                            variant="contained"
                                            onClick={() => executar(rodada, () => recusarRodada(pedido.uuid, rodada.uuid, motivo || undefined))}
                                        >
                                            Recusar
                                        </Button>
                                    </Box>
                                </Collapse>
                            </>
                        )}
                    </Box>
                );
            })}
        </Paper>
    );
};
